import React, { useEffect, useRef, useState } from "react";
import { QuizQueue } from "../Quiz/quizQueue.ts";
import {
    advance,
    DropGameState,
    LANES,
    LandedFact,
    levelFor,
    LIVES,
    progressOf,
    startGame,
    submit,
} from "./dropGame.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

export interface DropGameResult {
    cleared: number;
    wrongGuesses: number;
    level: number;
    landed: LandedFact[];
    /** Every fact cleared, as shown ("7 x 8") - for the missed-facts store. */
    clearedFronts: string[];
}

interface DropGameRunProps {
    queue: QuizQueue;
    onFinish(result: DropGameResult): void;
}

/** Where a fact sits across the field: the middle of its lane. */
function laneLeft(lane: number): string {
    return `${((lane + 0.5) * 100) / LANES}%`;
}

/**
 * One game: the falling field, the answer box beneath it, and a pause button.
 *
 * The engine is pure (`dropGame.ts`); this only feeds it animation-frame
 * deltas and Enter presses. State lives in a ref, with a snapshot published per
 * frame for rendering - not useReducer, because `advance` draws from the queue
 * and StrictMode runs reducers twice (ADR 0053 §8).
 *
 * Play time pauses (§4): on the Pause button, and by itself when the tab is
 * hidden. The field is hidden while paused, so pausing is not a way to read
 * every falling fact at leisure. Resuming is always a press.
 *
 * As on the quiz, focus stays in the answer box - including through the
 * countdown, so a phone's keyboard is already up when the first fact appears.
 */
export function DropGameRun({ queue, onFinish }: DropGameRunProps) {
    const t = useTranslation();
    const stateRef = useRef<DropGameState>(startGame());
    const [view, setView] = useState<DropGameState>(stateRef.current);
    const [paused, setPaused] = useState(false);
    const [typed, setTyped] = useState("");
    const [announcement, setAnnouncement] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const reported = useRef(false);

    const over = view.phase === "over";

    useEffect(() => {
        if (paused || over) {
            return;
        }
        let frame = 0;
        let last = performance.now();
        const tick = (now: number) => {
            // advance() clamps a negative or oversized delta itself.
            const next = advance(stateRef.current, now - last, queue);
            last = now;
            stateRef.current = next;
            setView(next);
            if (next.phase !== "over") {
                frame = requestAnimationFrame(tick);
            }
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [paused, over, queue]);

    useEffect(() => {
        const onVisibility = () => {
            if (document.hidden) {
                setPaused(true);
            }
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, []);

    useEffect(() => {
        if (!paused) {
            inputRef.current?.focus();
        }
    }, [paused]);

    useEffect(() => {
        if (over && !reported.current) {
            reported.current = true;
            onFinish({
                cleared: view.cleared,
                wrongGuesses: view.wrongGuesses,
                level: levelFor(view.cleared),
                landed: view.landed,
                clearedFronts: view.clearedFronts,
            });
        }
    }, [over, view, onFinish]);

    // Lives lost and new levels go to a polite live region; the rest of the
    // game is visible text a screen reader can reach on its own.
    const event = view.lastEvent;
    useEffect(() => {
        if (!event) {
            return;
        }
        const s = stateRef.current;
        if (event.kind === "landed" && s.lives > 0) {
            setAnnouncement(
                s.lives === 1
                    ? t("flashCards.gameLastLife")
                    : format(t("flashCards.gameLifeLostTemplate"), { n: s.lives })
            );
        } else if (event.kind === "cleared" && s.cleared > 0 && levelFor(s.cleared) > levelFor(s.cleared - 1)) {
            setAnnouncement(format(t("flashCards.gameLevelUpTemplate"), { n: levelFor(s.cleared) }));
        }
        // Keyed on the event's sequence number: each event announces once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event?.seq]);

    function submitAnswer() {
        if (paused) {
            return;
        }
        const next = submit(stateRef.current, typed);
        stateRef.current = next;
        setView(next);
        setTyped("");
    }

    const level = levelFor(view.cleared);
    const countdownSeconds = Math.max(1, Math.ceil(view.countdownLeftMs / 1000));
    const verdictClass =
        event?.kind === "cleared" ? " quizInput--correct" : event?.kind === "wrong" ? " quizInput--wrong" : "";

    return (
        <div className="quizRun dropRun">
            <div className="quizStatus dropStatus">
                <span className="quizScore">{format(t("flashCards.scoreTemplate"), { n: view.cleared })}</span>
                <span className="dropLevel">{format(t("flashCards.gameLevelTemplate"), { n: level })}</span>
                <span
                    className="dropLives"
                    role="img"
                    aria-label={
                        view.lives === 1
                            ? t("flashCards.gameOneLifeAria")
                            : format(t("flashCards.gameLivesAriaTemplate"), { n: view.lives })
                    }
                >
                    {"♥".repeat(view.lives)}
                    <span className="dropLivesLost">{"♥".repeat(LIVES - view.lives)}</span>
                </span>
            </div>

            <div className="dropField">
                {paused ? (
                    <div className="dropOverlay">
                        <p className="dropOverlayTitle">{t("flashCards.gamePaused")}</p>
                        <p className="dropOverlayHint">{t("flashCards.gamePausedHint")}</p>
                    </div>
                ) : view.phase === "countdown" ? (
                    <div className="dropOverlay" aria-live="assertive">
                        <p className="quizCountdownNumber">{countdownSeconds}</p>
                        <p className="dropOverlayHint">{t("flashCards.getReady")}</p>
                    </div>
                ) : (
                    <>
                        {view.drops.map((drop) => (
                            <div
                                key={drop.id}
                                className="dropFact"
                                style={{ left: laneLeft(drop.lane), "--p": progressOf(drop) } as React.CSSProperties}
                            >
                                {drop.front}
                            </div>
                        ))}
                        {view.bursts.map((burst) => (
                            <div
                                key={`burst-${burst.id}`}
                                className="dropBurst"
                                aria-hidden="true"
                                style={{ left: laneLeft(burst.lane), "--p": burst.progress } as React.CSSProperties}
                            >
                                {burst.answer} ✓
                            </div>
                        ))}
                        {event?.kind === "landed" && (
                            <div key={`landed-${event.seq}`} className="dropLandedFlash" aria-hidden="true" />
                        )}
                    </>
                )}
                <div className="dropGround" aria-hidden="true" />
            </div>

            <form
                className="quizCard dropCard"
                onSubmit={(e) => {
                    e.preventDefault();
                    submitAnswer();
                }}
            >
                <input
                    ref={inputRef}
                    className={`quizInput dropInput${verdictClass}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value.replace(/[^\d]/g, ""))}
                    onKeyDown={(e) => {
                        // As on the quiz: implicit form submission on Enter
                        // does not always fire, and this game is played on Enter.
                        if (e.key === "Enter") {
                            e.preventDefault();
                            submitAnswer();
                        }
                    }}
                    disabled={paused || over}
                    aria-label={t("flashCards.gameAnswerAria")}
                />
                <button type="submit" className="quizSubmit" disabled={paused || over}>
                    {t("flashCards.enterButton")}
                </button>
            </form>

            <button type="button" className="quizFinish dropPause" onClick={() => setPaused((p) => !p)} disabled={over}>
                {paused ? t("flashCards.gameResume") : t("flashCards.gamePause")}
            </button>

            <p className="dropAnnouncer" aria-live="polite">
                {announcement}
            </p>

            <p className="quizHint">{t("flashCards.gameHint")}</p>
        </div>
    );
}
